import React, { useState } from "react";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import { X, CalendarClock, User, MapPin } from "lucide-react";
import { Meeting } from "../../../../../shared/src/types/Meeting";
import {
  useEditMeetingMutation,
  useDeleteMeetingMutation,
} from "../../../api/calendarApi";

interface Props {
  date: Date;
  meetings: Meeting[];
  onClose: () => void;
  refreshMeetings: () => void; 
}

const DailyMeetingsSidebar: React.FC<Props> = ({
  date,
  meetings,
  onClose,
  refreshMeetings,
}) => {
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Meeting>>({});
  const [editMeeting] = useEditMeetingMutation();
  const [deleteMeeting] = useDeleteMeetingMutation();

  const handleEditClick = (meeting: Meeting) => {
    console.log(meeting);
    setEditingMeetingId(meeting.id);
    setFormData(meeting);
  };

  const handleCancelEdit = () => {
    setEditingMeetingId(null);
    setFormData({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
      if (!editingMeetingId) return;
    try {
      await editMeeting({
      id: editingMeetingId,
      data: {
        date: formData.date,
        location: formData.location,
        attendees: formData.attendees,
      },
    }).unwrap();
     alert("הפגישה עודכנה");
    setEditingMeetingId(null);
    setFormData({});
    await refreshMeetings(); 
    } catch {
      alert("שגיאה בעדכון הפגישה");
    }
  };

  const handleDelete = async (id: string | undefined) => {
    if (!id) {
      alert("שגיאה: מזהה הפגישה חסר");
      return;
    }

    const confirmed = window.confirm("האם את בטוחה שברצונך למחוק את הפגישה?");
    if (!confirmed) return;

    try {
      await deleteMeeting(id).unwrap();
      alert("הפגישה נמחקה");
      await refreshMeetings(); 
    } catch {
      alert("שגיאה במחיקת הפגישה");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end">
      <div className="w-full max-w-md bg-secondary h-full shadow-2xl overflow-y-auto rounded-l-2xl">
        <div className="bg-gradient-to-r from-primary to-highlight p-6 text-white rounded-tl-2xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold">פגישות ליום</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-secondary/20 hover:bg-secondary/30 transition"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <p className="text-sm font-medium">
            {format(date, "EEEE, d MMMM yyyy", { locale: he })}
          </p>
        </div>

        <div className="p-6">
          {meetings.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarClock className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-text-main mb-2">
                אין פגישות מתוכננות
              </h4>
              <p className="text-gray-500">לא נמצאו פגישות ליום זה</p>
            </div>
          ) : (
            <div className="space-y-4">
              {meetings.map((meeting) => (
                <div
                  key={meeting.id}
                  className="bg-background border border-border rounded-xl p-4 hover:shadow-card transition-all"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-text-main text-lg">
                      פגישה #{meeting.meeting_number}
                    </h4>
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                     {format(new Date(new Date(meeting.date).getTime() - 3 * 60 * 60 * 1000), "HH:mm")}
                    </span>
                  </div>

                  {meeting.location && (
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">מיקום: {meeting.location}</span>
                    </div>
                  )}

                  {meeting.attendees?.length > 0 && (
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <User className="w-4 h-4" />
                      <span className="text-sm">
                        משתתפים: {meeting.attendees.join(", ")}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-end mt-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditClick(meeting)}
                        className="text-blue-600 hover:underline"
                      >
                        ערוך
                      </button>
                      <button
                        onClick={() => handleDelete(meeting.id)}
                        className="text-red-600 hover:underline"
                      >
                        מחק
                      </button>
                    </div>
                  </div>

                  {editingMeetingId === meeting.id && (
  <div className="mt-4 space-y-2 border-t pt-3">
    <div>
      <label className="block text-sm font-medium">מספר פגישה:</label>
      <input
        type="number"
        name="meeting_number"
        value={formData.meeting_number || ""}
        disabled
        className="w-full border p-1 rounded bg-gray-100 text-gray-500"
      />
    </div>
    <div>
      <label className="block text-sm font-medium">תאריך:</label>
      <input
        type="datetime-local"
        name="date"
        value={formData.date || ""}
        onChange={handleInputChange}
        className="w-full border p-1 rounded"
      />
    </div>
    <div>
      <label className="block text-sm font-medium">מיקום:</label>
      <input
        type="text"
        name="location"
        value={formData.location || ""}
        onChange={handleInputChange}
        className="w-full border p-1 rounded"
      />
    </div>
    <div>
      <label className="block text-sm font-medium">משתתפים (מופרדים בפסיקים):</label>
      <input
        type="text"
        name="attendees"
        value={formData.attendees?.join(", ") || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            attendees: e.target.value.split(",").map((s) => s.trim()),
          }))
        }
        className="w-full border p-1 rounded"
      />
    </div>
    <div className="flex gap-2 mt-2">
      <button
        onClick={handleSave}
        className="bg-green-600 text-white px-3 py-1 rounded"
      >
        שמור
      </button>
      <button
        onClick={handleCancelEdit}
        className="bg-gray-300 px-3 py-1 rounded"
      >
        ביטול
      </button>
    </div>
  </div>
)}

                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyMeetingsSidebar;
